import classnames from 'classnames';
import debounce from 'just-debounce';
import { equals, head } from 'ramda';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { Key } from 'ts-keycode-enum';
import { CloseButton, IconButton, Input, Text, WixIcons } from '../../components/common';
import { MediaType } from '../../constants/MediaType';
import { MainSource } from '../../constants/Source';
import { makeCreatorLink } from '../../utils/creatorLink';
import { onHotKeys } from '../../utils/dom/domEvents';
import {
  UnsplashClickOrigin,
  UnsplashTarget,
} from '../../utils/hoc/bi/withTrackUnsplashClick/withTrackUnsplashClick.types';
import { JumpTo } from '../../utils/hoc/withItemsReducer/withItemsReducer.types';
import { useTrackPreviewActions } from '../../utils/hooks/bi/useTrackPreviewActions';
import {
  ClickOrigin as PreviewClickOrigin,
  ClickOrigin,
  PreviewTarget,
} from '../../utils/hooks/bi/useTrackPreviewActions/useTrackPreviewActions.types';
import { useTrackUnsplashClick } from '../../utils/hooks/bi/useTrackUnsplashClick';
import { MediaManagerSDKAnyItem } from '../../utils/media-manager-sdk';
import {
  hasDuration,
  hasResolution,
  isFile,
  isSiteIconFile,
  selectExpandedItemToJump,
} from '../../utils/media-manager-sdk/items';
import { isUnsplash } from '../../utils/media-manager-sdk/sources';
import { RouteBuilder } from '../../utils/RouteHelpers';
import { convertMSSToMMSS } from '../../utils/time';
import { MAX_DEMONSTRATION_HEIGHT, MAX_DEMONSTRATION_WIDTH } from './ExpandedView.constants';
import styles from './ExpandedView.scss';
import { IDemonstrationDimension, IOuter } from './ExpandedView.types';
import { ItemDemonstration } from './ItemDemonstration';
import { ItemsPicker } from './ItemsPicker';

const getItemInfo = (item: MediaManagerSDKAnyItem) => {
  if (isFile(item)) {
    switch (item.mediaType) {
      case MediaType.Picture:
      case MediaType.ThreeD:
      case MediaType.SecurePicture:
      case MediaType.VectorArt:
      case MediaType.SiteIcon:
        return hasResolution(item) ? (
          <FormattedMessage id="common.resolution-light" values={{ width: item.width, height: item.height }} />
        ) : null;

      case MediaType.Music:
      case MediaType.SecureMusic:
      case MediaType.Video:
      case MediaType.SecureVideo:
        return hasDuration(item) && convertMSSToMMSS(item.fileInput.duration);

      default:
        return null;
    }
  }

  return null;
};

const backgroundContainStyle = { backgroundSize: 'contain' };

export const ExpandedView: FC<IOuter> = ({
  items,
  editingItem,
  className,
  footerContent,
  onClose,
  onRenameCancel,
  onNameChange,
  onRenameComplete,
  demonstrationThumbnailStyle: demonstrationThumbnailStyleFromProps,
  jumpToPrevItem,
  jumpToNextItem,
  totalItemsCount,
}) => {
  const [activeItem, setActiveItem] = useState(head(items));
  const [demonstrationWrapperDimension, setDemonstrationWrapperDimension] = useState(null);

  const { trackPreviewActions, trackPreviewActionsPreviewBottomNavigationClicked } = useTrackPreviewActions();
  const { trackUnsplashLinkClickClicked } = useTrackUnsplashClick();

  const setActiveItemFromPicker = useCallback(
    (activeItem) => {
      setActiveItem(activeItem);
      trackPreviewActionsPreviewBottomNavigationClicked(activeItem);
    },
    [setActiveItem, trackPreviewActionsPreviewBottomNavigationClicked],
  );

  const handleNameChange = useCallback((event) => onNameChange(event.target.value), [onNameChange]);

  const areMultipleItemsExpanded = items.length > 1;
  const isEditing = activeItem && editingItem && activeItem.id === editingItem.id;
  const info = getItemInfo(activeItem);
  const hideArrows = totalItemsCount < 2;

  const demonstrationThumbnailStyle =
    demonstrationWrapperDimension &&
    isFile(activeItem) &&
    (activeItem.width > demonstrationWrapperDimension.width || activeItem.height > demonstrationWrapperDimension.height)
      ? backgroundContainStyle
      : demonstrationThumbnailStyleFromProps;

  const demonstrationRef = useRef();
  const updateDemonstrationDimension = useCallback(() => {
    if (!demonstrationRef.current) {
      return;
    }

    const { offsetWidth, offsetHeight }: HTMLDivElement = demonstrationRef.current;

    const newDemonstrationWrapperDimension: IDemonstrationDimension = {
      width: offsetWidth,
      height: offsetHeight,
    };

    if (!equals(demonstrationWrapperDimension, newDemonstrationWrapperDimension)) {
      setDemonstrationWrapperDimension(newDemonstrationWrapperDimension);
    }
  }, [demonstrationWrapperDimension, setDemonstrationWrapperDimension]);

  const performClose = useCallback(
    (clickOrigin: PreviewClickOrigin) => {
      onClose();

      trackPreviewActions(
        {
          target: PreviewTarget.Closed,
          fileId: activeItem && activeItem.id,
          clickOrigin,
        },
        activeItem,
      );
    },
    [onClose, trackPreviewActions, activeItem],
  );

  const handleCloseButtonClick = useCallback(() => performClose(ClickOrigin.CloseIcon), [performClose]);

  const jumpToSelectedExpandedItem = useCallback(
    (jumpTo: JumpTo) => {
      if (isEditing) {
        return;
      }

      const newItem = selectExpandedItemToJump({ activeItem, items, jumpTo });
      trackPreviewActions(
        {
          fileId: newItem.id,
          target: PreviewTarget.Opened,
          clickOrigin: jumpTo === JumpTo.Next ? ClickOrigin.RightArrow : ClickOrigin.LeftArrow,
        },
        newItem,
      );

      setActiveItem(newItem);
    },
    [activeItem, items, setActiveItem, isEditing, trackPreviewActions],
  );

  const handleJumpToPrevItem = useCallback(() => {
    if (areMultipleItemsExpanded) {
      jumpToSelectedExpandedItem(JumpTo.Prev);
    }
  }, [areMultipleItemsExpanded, jumpToSelectedExpandedItem]);

  const handleJumpToPrevItemFromArrow = useCallback(() => {
    if (areMultipleItemsExpanded) {
      jumpToSelectedExpandedItem(JumpTo.Prev);
      return;
    }

    jumpToPrevItem();

    trackPreviewActions(
      {
        clickOrigin: PreviewClickOrigin.LeftArrow,
        target: PreviewTarget.Opened,
      },
      activeItem,
    );
  }, [areMultipleItemsExpanded, jumpToSelectedExpandedItem, jumpToPrevItem, trackPreviewActions, activeItem]);

  const handleJumpToNextItem = useCallback(() => {
    if (areMultipleItemsExpanded) {
      jumpToSelectedExpandedItem(JumpTo.Next);
    }
  }, [areMultipleItemsExpanded, jumpToSelectedExpandedItem]);

  const handleJumpToNextItemFromArrow = useCallback(() => {
    if (areMultipleItemsExpanded) {
      jumpToSelectedExpandedItem(JumpTo.Next);
      return;
    }

    jumpToNextItem();

    trackPreviewActions(
      {
        clickOrigin: PreviewClickOrigin.RightArrow,
        target: PreviewTarget.Opened,
      },
      activeItem,
    );
  }, [areMultipleItemsExpanded, jumpToSelectedExpandedItem, jumpToNextItem, trackPreviewActions, activeItem]);

  useEffect(() => {
    const onWindowResize = debounce(updateDemonstrationDimension, 150);
    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [updateDemonstrationDimension]);

  useEffect(updateDemonstrationDimension);

  useEffect(() => {
    if (!areMultipleItemsExpanded) {
      setActiveItem(head(items));
    }
  }, [items, areMultipleItemsExpanded, setActiveItem]);

  useEffect(() => {
    const jumpToNextItemHotKey = onHotKeys(Key.RightArrow, handleJumpToNextItem);
    const jumpToPrevItemHotKey = onHotKeys(Key.LeftArrow, handleJumpToPrevItem);
    const closeOnHotKey = onHotKeys(Key.Escape, (event) => {
      performClose(PreviewClickOrigin.Keyboard);
      event.stopImmediatePropagation();
    });

    window.addEventListener('keydown', jumpToNextItemHotKey, false);
    window.addEventListener('keydown', jumpToPrevItemHotKey, false);
    window.addEventListener('keydown', closeOnHotKey, true);

    return () => {
      window.removeEventListener('keydown', jumpToNextItemHotKey, false);
      window.removeEventListener('keydown', jumpToPrevItemHotKey, false);
      window.removeEventListener('keydown', closeOnHotKey, true);
    };
  }, [handleJumpToNextItem, handleJumpToPrevItem, performClose]);

  const creatorName = isFile(activeItem) ? activeItem.creator : null;

  const history = useHistory();

  const searchCreatorWorks = useCallback(
    (event) => {
      if (!isUnsplash(activeItem.source)) {
        event.preventDefault();
        return history.push(RouteBuilder.Search(MainSource.MediaFromWix, `creator:"${creatorName}"`));
      }

      trackUnsplashLinkClickClicked(UnsplashTarget.DesignerName, UnsplashClickOrigin.Preview);
    },
    [activeItem.source, creatorName, history, trackUnsplashLinkClickClicked],
  );

  const shouldShowCreatorInfo = Boolean(
    creatorName && !isEditing && (isUnsplash(activeItem.source) || !activeItem.name),
  );

  const creatorLink = makeCreatorLink(activeItem);

  const itemDemonstrationParams = useMemo(
    () => ({ keepOriginalSize: isFile(activeItem) && isSiteIconFile(activeItem) }),
    [activeItem],
  );

  return (
    <div className={classnames(styles.expandView, className)} data-hook="expanded-view">
      <div className={styles.header}>
        <div className={classnames(styles.infoContainer, { [styles.oneLine]: !info })}>
          {shouldShowCreatorInfo ? (
            <Text tagName="div" weight="thin" size="small">
              <FormattedMessage
                id="unsplash.expanded-view.creator"
                values={{
                  creator: (
                    <Text weight="normal" size="small">
                      <a
                        href={creatorLink}
                        target="_blank"
                        className={styles.creatorLink}
                        onClick={searchCreatorWorks}
                        rel="noreferrer"
                      >
                        {creatorName}
                      </a>
                    </Text>
                  ),
                }}
              />
            </Text>
          ) : (
            <Text
              tagName="div"
              weight="normal"
              size="small"
              className={styles.fileName}
              dataHook="expanded-view-file-name"
            >
              {isEditing ? (
                <Input
                  value={editingItem.name}
                  size="small"
                  onChange={handleNameChange}
                  onEnterPressed={onRenameComplete}
                  onBlur={onRenameCancel}
                  onEscapePressed={onRenameCancel}
                  autoFocus
                  dataHook="expanded-view-file-name-edit-input"
                />
              ) : (
                activeItem.name
              )}
            </Text>
          )}

          {info && (
            <Text tagName="div" weight="thin" size="small">
              {info}
            </Text>
          )}
        </div>

        <div className={styles.actionsContainer}>
          <CloseButton
            onClick={handleCloseButtonClick}
            dataHook="expanded-view-close-button"
            size="large"
            skin="dark"
          />
        </div>
      </div>

      <div className={styles.wrapper}>
        <div className={styles.content}>
          <div
            className={classnames(styles.arrowWrapper, { [styles.hidden]: hideArrows })}
            data-hook="expanded-view-prev-button-wrapper"
          >
            <IconButton
              skin="standard"
              priority="secondary"
              onClick={handleJumpToPrevItemFromArrow}
              dataHook="expanded-view-prev-button"
            >
              <WixIcons.ChevronLeft />
            </IconButton>
          </div>

          <div className={styles.demonstrationContainer}>
            <ItemDemonstration
              className={styles.demonstration}
              item={activeItem}
              width={MAX_DEMONSTRATION_WIDTH}
              height={MAX_DEMONSTRATION_HEIGHT}
              thumbnailStyle={demonstrationThumbnailStyle}
              saveRef={demonstrationRef}
              wrapperDimension={demonstrationWrapperDimension}
              params={itemDemonstrationParams}
            />
          </div>

          <div
            className={classnames(styles.arrowWrapper, { [styles.hidden]: hideArrows })}
            data-hook="expanded-view-next-button-wrapper"
          >
            <IconButton
              skin="standard"
              priority="secondary"
              onClick={handleJumpToNextItemFromArrow}
              dataHook="expanded-view-next-button"
            >
              <WixIcons.ChevronRight />
            </IconButton>
          </div>
        </div>

        {areMultipleItemsExpanded && (
          <ItemsPicker setActiveItem={setActiveItemFromPicker} activeItem={activeItem} items={items} />
        )}
      </div>

      <div className={styles.footer}>{footerContent}</div>
    </div>
  );
};
